import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests
})

// Add trailing slash to avoid 307 redirects
const withSlash = (path) => path.endsWith('/') ? path : path + '/'

// Sources
export const sourcesAPI = {
  getAll: (params) => api.get(withSlash('/sources'), { params }).then(r => r.data),
  getStats: () => api.get(withSlash('/sources/stats')).then(r => r.data),
  getById: (id) => api.get(withSlash(`/sources/${id}`)).then(r => r.data),
  create: (data) => api.post(withSlash('/sources'), data).then(r => r.data),
  update: (id, data) => api.put(withSlash(`/sources/${id}`), data).then(r => r.data),
  delete: (id) => api.delete(withSlash(`/sources/${id}`)).then(r => r.data),
}

// Authors
export const authorsAPI = {
  getAll: (params) => api.get(withSlash('/authors'), { params }).then(r => r.data),
  getStats: () => api.get(withSlash('/authors/stats')).then(r => r.data),
  getPending: (limit = 20) => api.get(withSlash('/authors/pending'), { params: { limit } }).then(r => r.data),
  getRejected: (limit = 50) => api.get(withSlash('/authors/rejected'), { params: { limit } }).then(r => r.data),
  getById: (id) => api.get(withSlash(`/authors/${id}`)).then(r => r.data),
  approveAuto: (id) => api.put(withSlash(`/authors/${id}/approve-auto`)).then(r => r.data),
  approveManual: (id) => api.put(withSlash(`/authors/${id}/approve-manual`)).then(r => r.data),
  reject: (id) => api.put(withSlash(`/authors/${id}/reject`)).then(r => r.data),
  pause: (id) => api.put(withSlash(`/authors/${id}/pause`)).then(r => r.data),
  restore: (id) => api.put(withSlash(`/authors/${id}/restore`)).then(r => r.data),
  bulkAction: (data) => api.post(withSlash('/authors/bulk'), data).then(r => r.data),
  update: (id, data) => api.put(withSlash(`/authors/${id}`), data).then(r => r.data),
}

// Images
export const imagesAPI = {
  getAll: (params) => api.get(withSlash('/images'), { params }).then(r => r.data),
  getStats: () => api.get(withSlash('/images/stats')).then(r => r.data),
  getReadyToPublish: (limit = 20) => api.get(withSlash('/images/ready-to-publish'), { params: { limit } }).then(r => r.data),
  getById: (id) => api.get(withSlash(`/images/${id}`)).then(r => r.data),
  update: (id, data) => api.put(withSlash(`/images/${id}`), data).then(r => r.data),
  reject: (id) => api.put(withSlash(`/images/${id}/reject`)).then(r => r.data),
  approve: (id) => api.put(withSlash(`/images/${id}/approve`)).then(r => r.data),
  publish: (id, target = 'agr') => api.post(withSlash(`/images/${id}/publish`), null, { params: { target } }).then(r => r.data),
  delete: (id) => api.delete(withSlash(`/images/${id}`)).then(r => r.data),
  setCategory: (id, categoryId) => api.put(withSlash(`/images/${id}/category/${categoryId}`)).then(r => r.data),
  unpublish: (id) => api.put(withSlash(`/images/${id}/unpublish`)).then(r => r.data),

  // Engagement & Quality
  getTop: (params) => api.get(withSlash('/images/top'), { params }).then(r => r.data),
  getByTier: (tier, params) => api.get(withSlash(`/images/by-tier/${tier}`), { params }).then(r => r.data),
  getTierCounts: (params) => api.get(withSlash('/images/tier-counts'), { params }).then(r => r.data),
  getEngagementStats: () => api.get(withSlash('/images/engagement/stats')).then(r => r.data),
  recalculateScores: () => api.post(withSlash('/images/engagement/recalculate')).then(r => r.data),
  trackEvent: (id, eventType, count = 1) => api.post(withSlash(`/images/${id}/track/${eventType}`), null, { params: { count } }).then(r => r.data),
  trackBulk: (events) => api.post(withSlash('/images/track/bulk'), { events }).then(r => r.data),
}

// Queue
export const queueAPI = {
  getStatus: () => api.get(withSlash('/queue/status')).then(r => r.data),
  getStats: () => api.get(withSlash('/queue/stats')).then(r => r.data),
  getTasks: (params) => api.get(withSlash('/queue'), { params }).then(r => r.data),
  createTask: (data) => api.post(withSlash('/queue'), data).then(r => r.data),
  deleteTask: (id) => api.delete(withSlash(`/queue/${id}`)).then(r => r.data),
  retryTask: (id) => api.post(withSlash(`/queue/${id}/retry`)).then(r => r.data),
  recoverStale: (params) => api.post(withSlash('/queue/recover-stale'), null, { params }).then(r => r.data),
  clearCompleted: (hours = 24) => api.post(withSlash('/queue/clear-completed'), null, { params: { older_than_hours: hours } }).then(r => r.data),
}

// System status
export const statusAPI = {
  getSystem: () => api.get(withSlash('/status')).then(r => r.data),
  getQuick: () => api.get(withSlash('/status/quick')).then(r => r.data),
  rebuildLojsHomeFeed: () => api.post(withSlash('/lojs-maintenance/rebuild-home-feed')).then(r => r.data),
  rebuildLojsRelatedCards: (limit) => api.post(withSlash('/lojs-maintenance/rebuild-related-cards'), null, { params: limit ? { limit } : {} }).then(r => r.data),
  getRelatedWaveMode: () => api.get(withSlash('/lojs-maintenance/related-wave-mode')).then(r => r.data),
  setRelatedWaveMode: (mode) => api.post(withSlash(`/lojs-maintenance/related-wave-mode/${mode}`)).then(r => r.data),
  getWebmasterWaveMode: () => api.get(withSlash('/lojs-maintenance/webmaster-wave-mode')).then(r => r.data),
  setWebmasterWaveMode: (mode) => api.post(withSlash(`/lojs-maintenance/webmaster-wave-mode/${mode}`)).then(r => r.data),
}

// Auth
export const authAPI = {
  login: (username, password) => api.post(withSlash('/auth/login'), { username, password }).then(r => r.data),
  logout: () => api.post(withSlash('/auth/logout')).then(r => r.data),
  check: () => api.get(withSlash('/auth/check')).then(r => r.data),
}

// Categories - base path needs trailing slash, subpaths don't
export const categoriesAPI = {
  // Base categories
  getAll: (params) => api.get('/categories/', { params }).then(r => r.data),
  getStats: () => api.get('/categories/stats').then(r => r.data),
  getById: (id) => api.get(`/categories/${id}`).then(r => r.data),
  create: (data) => api.post('/categories/', data).then(r => r.data),
  update: (id, data) => api.put(`/categories/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/categories/${id}`).then(r => r.data),

  // Unmapped source categories
  getUnmapped: (params) => api.get('/categories/unmapped/list', { params }).then(r => r.data),
  getUnmappedCount: (params) => api.get('/categories/unmapped/count', { params }).then(r => r.data),

  // Source category mapping
  mapSource: (data) => api.post('/categories/map-source', data).then(r => r.data),
  unmapSource: (mappingId) => api.post(`/categories/unmap-source/${mappingId}`).then(r => r.data),
  bulkMapSource: (data) => api.post('/categories/map-source/bulk', data).then(r => r.data),

  // Enrichment
  enrich: (mappingId) => api.post(`/categories/enrich/${mappingId}`).then(r => r.data),
  createAndMap: (data) => api.post('/categories/create-and-map', null, { params: data }).then(r => r.data),

  // Target mappings
  getTargetMappings: (params) => api.get('/categories/target-mappings', { params }).then(r => r.data),
  mapTarget: (data) => api.post('/categories/map-target', data).then(r => r.data),
  deleteTargetMapping: (id) => api.delete(`/categories/target-mapping/${id}`).then(r => r.data),

  // Resolve
  resolve: (aggCategoryId, platform) => api.get(`/categories/resolve/${aggCategoryId}/${platform}`).then(r => r.data),

  // Top relevant (dynamic scoring)
  getTopRelevant: () => api.get('/categories/top-relevant').then(r => r.data),
}

// Storage Monitor
export const storageAPI = {
  getCurrent: () => api.get(withSlash('/storage/current')).then(r => r.data),
  getHistory: (period = '7d') => api.get(withSlash('/storage/history'), { params: { period } }).then(r => r.data),
  getAlerts: () => api.get(withSlash('/storage/alerts')).then(r => r.data),
  triggerCollection: () => api.post(withSlash('/storage/collect')).then(r => r.data),
}

// User Uploads Moderation
export const userUploadsAPI = {
  getPending: (params) => api.get('/user-uploads/pending', { params }).then(r => r.data),
  approve: (id) => api.put(`/user-uploads/${id}/approve`).then(r => r.data),
  reject: (id) => api.put(`/user-uploads/${id}/reject`).then(r => r.data),
  getStats: () => api.get('/user-uploads/stats').then(r => r.data),
}

// Catalog (AGR) — реально опубликованные открытки
export const catalogAPI = {
  getPostcards: (params) => api.get(withSlash('/catalog/postcards'), { params }).then(r => r.data),
  getCategories: () => api.get(withSlash('/catalog/categories')).then(r => r.data),
  deletePostcard: (id) => api.delete(withSlash(`/catalog/postcards/${id}`)).then(r => r.data),
}

// Webmaster Audit
export const webmasterAuditAPI = {
  getBatches: (limit = 20) => api.get('/webmaster-audit/batches', { params: { limit } }).then(r => r.data),
  getBatch: (id, rowsLimit = 200) => api.get(`/webmaster-audit/batches/${id}`, { params: { rows_limit: rowsLimit } }).then(r => r.data),
  getRows: (params) => api.get('/webmaster-audit/rows', { params }).then(r => r.data),
  refreshBatch: (id, onlyUnresolved = false) =>
    api.post(`/webmaster-audit/batches/${id}/refresh-status`, null, { params: { only_unresolved: onlyUnresolved } }).then(r => r.data),
  enqueueRegeneration: (id, params = {}) =>
    api.post(`/webmaster-audit/batches/${id}/enqueue-regeneration`, null, { params }).then(r => r.data),
  uploadCsv: async ({ file, issue_type, source_site = 'lojs' }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('issue_type', issue_type)
    formData.append('source_site', source_site)
    const response = await api.post('/webmaster-audit/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  }
}

// Tests Dashboard - без trailing slash
export const testsAPI = {
  getSuites: () => api.get('/tests/suites').then(r => r.data),
  getRuns: (params) => api.get('/tests/runs', { params }).then(r => r.data),
  getRun: (id) => api.get(`/tests/runs/${id}`).then(r => r.data),
  getArtifacts: (runId) => api.get(`/tests/runs/${runId}/artifacts`).then(r => r.data),
  runTests: (data) => api.post('/tests/run', data).then(r => r.data),
  getStats: () => api.get('/tests/stats').then(r => r.data),
  clearOldRuns: (data) => api.post('/tests/clear-old', data).then(r => r.data),
}

// Game Monitor - игра "Путь Солнышка" (без trailing slash)
export const gameAPI = {
  getStats: () => api.get('/game/stats').then(r => r.data),
  getUsers: (params) => api.get('/game/users', { params }).then(r => r.data),
  getTop: (category = 'level', limit = 20) => api.get('/game/top', { params: { category, limit } }).then(r => r.data),
  getUserDetail: (userId) => api.get(`/game/users/${userId}`).then(r => r.data),
  getQuestStats: () => api.get('/game/quests/stats').then(r => r.data),
  getEmotionStats: () => api.get('/game/emotions/stats').then(r => r.data),
  getCronStatus: () => api.get('/game/cron/status').then(r => r.data),
  runCronTask: (taskName) => api.post(`/game/cron/run/${taskName}`).then(r => r.data),
  getActivityChart: (days = 7) => api.get('/game/activity/chart', { params: { days } }).then(r => r.data),
  // Admin actions
  resetAllPlayers: () => api.post('/game/admin/reset-all-players').then(r => r.data),
  resetPlayer: (userId) => api.post(`/game/admin/reset-player/${userId}`).then(r => r.data),
  initPlayer: (userId) => api.post(`/game/admin/init-player/${userId}`).then(r => r.data),
}

export default api
